import Styles from './styles.module.css';
import React, { useRef, useState } from 'react';
import { getFirestore, addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import DefaultStation, { schoolOptions } from '../../../package/school/index';

export type GraduationEditing = {
  id: string;
  name: string;
  school: string;
  graduation_years: string;
  postion: string;
};

type HeaderEditProps = {
  companyId: string;
  setMake: (open: boolean) => void;
  // 渡したときは、その卒業生の編集
  editing?: GraduationEditing | null;
};

const HeaderEdit: React.FC<HeaderEditProps> = ({ companyId, setMake, editing = null }) => {

  const db = getFirestore();

  const bodyRef = useRef<HTMLDivElement | null>(null);

  const [ name, setName ] = useState<string>(editing?.name ?? '');
  const [ school, setSchool ] = useState<string>(editing?.school ?? '');
  const [ graduationYears, setGraduationYears ] = useState<string>(editing?.graduation_years != null ? String(editing.graduation_years) : '');
  const [ postion, setPositon ] = useState<string>(editing?.postion ?? '');

  const [ errorName, setErrorName ] = useState(false);
  const [ errorSchool, setErrorSchool ] = useState(false);
  const [ errorGraduationYears, setErrorGraduationYears ] = useState(false);
  const [ errorPostion, setErrorPostion ] = useState(false);

  // 保存時のバリデーション結果を1件だけ上部に表示する
  const [ errorMessage, setErrorMessage ] = useState<string>('');

  // 保存中のロック。「追加」の連打で addDoc が複数回走り、別IDの重複ドキュメントが
  // 作られるのを防ぐ(state は反映が遅れるため、押した瞬間に効く ref で判定する)。
  const savingRef = useRef<boolean>(false);
  const [ saving, setSaving ] = useState<boolean>(false);

  const showError = (message: string) => {
    setErrorMessage(message);
    bodyRef.current?.scrollTo({ top: 0 });
  };

  async function save() {
    if (savingRef.current) return;

    setErrorName(false);
    setErrorSchool(false);
    setErrorGraduationYears(false);
    setErrorPostion(false);
    setErrorMessage('');

    if (name === '') {
      setErrorName(true);
      showError('氏名を入力してください。');
    } else if (school === '' || !schoolOptions.includes(school)) {
      setErrorSchool(true);
      showError('出身学校を候補一覧から選択してください。');
    } else if (graduationYears === '' || !/^\d+$/.test(graduationYears)) {
      setErrorGraduationYears(true);
      showError('卒業年度を半角数字のみで入力してください。');
    } else if (postion === '') {
      setErrorPostion(true);
      showError('役職を入力してください。');
    } else {
      savingRef.current = true;
      setSaving(true);
      try {
        if (editing) {
          await updateDoc(doc(db, 'graduation', editing.id), {
            name: name,
            school: school,
            graduation_years: graduationYears,
            postion: postion,
            updated_at: new Date(),
          });
          setMake(false);
          return;
        }

        await addDoc(collection(db, 'graduation'), {
          companyId: companyId,
          name: name,
          school: school,
          graduation_years: graduationYears,
          postion: postion,
          time: new Date(),
        })
        .then(() => setMake(false));
      } catch (error) {
        console.error('エラーが発生しました', error);
        showError(`卒業生の${editing ? '保存' : '追加'}に失敗しました。時間をおいて、もう一度お試しください。`);
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    }

  }

  const inputClass = (hasError: boolean) =>
    hasError ? `${Styles.input} ${Styles.input_error}` : Styles.input;

  return (
    <div className={Styles.overlay} role='dialog' aria-modal='true'>
      <div className={Styles.scrim} onClick={() => setMake(false)} />
      <div className={Styles.card}>

        <div className={Styles.header}>
          <h3 className={Styles.headerTitle}>{editing ? '卒業生の編集' : '卒業生の追加'}</h3>
          <button type='button' className={Styles.headerBtn} disabled={saving} onClick={() => save()}>
            {saving ? (editing ? '保存中...' : '追加中...') : (editing ? '保存' : '追加')}
          </button>
        </div>

        <div className={Styles.body} ref={bodyRef}>

          {errorMessage && (
            <div className={Styles.errorBox} role='alert'>
              <p className={Styles.errorBox_label}>エラーメッセージ：</p>
              <p className={Styles.errorBox_text}>{errorMessage}</p>
            </div>
          )}

          {/* 名前 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>名前<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{name.length} / 15</span>
            </div>
            <input
              className={inputClass(errorName)}
              placeholder='鈴木 太郎'
              maxLength={15}
              type='text'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* 出身学校 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>出身学校<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{school.length} / 26</span>
            </div>
            <div className={errorSchool ? `${Styles.schoolWrap} ${Styles.schoolWrap_error}` : Styles.schoolWrap}>
              <DefaultStation value={school} setValue={setSchool} />
            </div>
          </div>

          {/* 卒業年度 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>卒業年度<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{graduationYears.length} / 4</span>
            </div>
            <input
              className={inputClass(errorGraduationYears)}
              placeholder='2010'
              maxLength={4}
              type='text'
              value={graduationYears}
              onChange={(e) => setGraduationYears(e.target.value)}
            />
          </div>

          {/* 役職 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>役職<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{postion.length} / 20</span>
            </div>
            <input
              className={inputClass(errorPostion)}
              placeholder='スタイリスト 2年目,店長 3年目 など'
              maxLength={20}
              type='text'
              value={postion}
              onChange={(e) => setPositon(e.target.value)}
            />
          </div>

        </div>
      </div>
    </div>
  );
}

export default HeaderEdit;
